// SPDX-License-Identifier: AGPL-3.0-or-later

import {ApplicationFlags, allowsIntent, type PrivilegedIntent} from '@fluxer/constants/src/BotConstants';

export interface DeveloperApplicationBot {
	id: string;
	username: string;
	discriminator: string;
	avatar: string | null;
	bio?: string | null;
	bio_localizations?: Record<string, string> | null;
	token?: string;
	banner?: string | null;
	flags?: number;
}

export interface DeveloperApplicationWire {
	id: string;
	name: string;
	redirect_uris: Array<string>;
	bot_public: boolean;
	bot_require_code_grant: boolean;
	client_secret?: string;
	bot?: DeveloperApplicationBot;
	flags?: number;
	intent_flags_pending?: number;
	approximate_guild_count?: number;
}

const INTENT_REQUEST_BITS: Record<PrivilegedIntent, number> = {
	presence: ApplicationFlags.GATEWAY_PRESENCE,
	guild_members: ApplicationFlags.GATEWAY_GUILD_MEMBERS,
	message_content: ApplicationFlags.GATEWAY_MESSAGE_CONTENT,
};

export function buildGatewayIntentFlags(
	application: DeveloperApplication,
	intent: PrivilegedIntent,
	enabled: boolean,
): number {
	let flags = 0;
	for (const [key, bit] of Object.entries(INTENT_REQUEST_BITS) as Array<[PrivilegedIntent, number]>) {
		const on =
			key === intent
				? enabled
				: allowsIntent(application.flags, key) || allowsIntent(application.intent_flags_pending, key);
		if (on) flags |= bit;
	}
	return flags;
}

type DeveloperApplicationInput = DeveloperApplicationWire | DeveloperApplication;

export class DeveloperApplication {
	readonly id: string;
	readonly name: string;
	readonly redirect_uris: Array<string>;
	readonly bot_public: boolean;
	readonly bot_require_code_grant: boolean;
	readonly client_secret?: string;
	readonly bot?: DeveloperApplicationBot;
	readonly flags: number;
	readonly intent_flags_pending: number;
	readonly approximate_guild_count?: number;

	constructor(application: DeveloperApplicationInput) {
		this.id = application.id;
		this.name = application.name;
		this.redirect_uris = application.redirect_uris ? [...application.redirect_uris] : [];
		this.bot_public = application.bot_public;
		this.bot_require_code_grant = application.bot_require_code_grant;
		this.flags = application.flags ?? 0;
		this.intent_flags_pending = application.intent_flags_pending ?? 0;
		this.approximate_guild_count = application.approximate_guild_count;
		if ('client_secret' in application) {
			this.client_secret = application.client_secret;
		}
		if (application.bot) {
			this.bot = {
				id: application.bot.id,
				username: application.bot.username,
				discriminator: application.bot.discriminator,
				avatar: application.bot.avatar,
				bio: application.bot.bio ?? null,
				bio_localizations: application.bot.bio_localizations ?? null,
				token: application.bot.token,
				banner: application.bot.banner ?? null,
				flags: application.bot.flags,
			};
		}
	}

	static from(application: DeveloperApplicationInput): DeveloperApplication {
		return new DeveloperApplication(application);
	}

	withUpdates(updates: Partial<DeveloperApplicationWire>): DeveloperApplication {
		return new DeveloperApplication({
			...this.toObject(),
			...updates,
			redirect_uris: updates.redirect_uris ?? this.redirect_uris,
			bot: updates.bot ?? this.bot,
		});
	}

	toObject(): DeveloperApplicationWire {
		return {
			id: this.id,
			name: this.name,
			redirect_uris: [...this.redirect_uris],
			bot_public: this.bot_public,
			bot_require_code_grant: this.bot_require_code_grant,
			client_secret: this.client_secret,
			flags: this.flags,
			intent_flags_pending: this.intent_flags_pending,
			approximate_guild_count: this.approximate_guild_count,
			bot: this.bot
				? {
						...this.bot,
						flags: this.bot.flags,
					}
				: undefined,
		};
	}
}
